import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://acbuy.asia';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === `/${slug}`);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (article) {
      document.title = `${article.title} | AcBuy Spreadsheet`;
    }
  }, [slug, article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-24">
          <div className="text-center">
            <h1 className="font-heading text-3xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
            <p className="text-foreground-500 mb-6">Sorry, this article could not be found.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-colors whitespace-nowrap"
            >
              Back to Home
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleUrl = `${SITE_URL}/guide${article.slug}`;

  return (
    <>
      <Navbar />
      <main>
        <article>
          <header className="pt-24 md:pt-28 pb-10 md:pb-14 bg-background-200/60">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <div className="max-w-3xl mx-auto">
                <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-6 flex-wrap">
                  <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">Home</Link>
                  <span>/</span>
                  <span className="text-foreground-600 whitespace-nowrap">{article.title}</span>
                </nav>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 leading-tight mb-4">
                  {article.h1}
                </h1>
                <p className="text-foreground-500 text-base md:text-lg leading-relaxed mb-6 max-w-2xl">
                  {article.intro}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
                  <span className="whitespace-nowrap">Updated: June 2026</span>
                  <span className="whitespace-nowrap">{article.readTime}</span>
                  <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">{article.tag}</span>
                </div>
              </div>
            </div>
          </header>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 max-w-5xl mx-auto">
              <aside className="lg:w-64 flex-shrink-0">
                <div className="lg:sticky lg:top-24 bg-background-100 rounded-lg border border-background-200/70 p-5">
                  <h4 className="font-heading text-sm font-bold text-foreground-900 mb-3 uppercase tracking-wider whitespace-nowrap">Related Articles</h4>
                  <nav className="flex flex-col gap-2">
                    {articles.filter(a => a.slug !== article.slug).slice(0, 8).map((item) => (
                      <Link
                        key={item.slug}
                        to={`/guide${item.slug}`}
                        className="text-xs text-foreground-600 hover:text-primary-500 transition-colors leading-snug cursor-pointer whitespace-nowrap"
                      >
                        {item.title}
                      </Link>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div
                  className="space-y-4 text-foreground-700 text-base leading-relaxed font-light article-content"
                  dangerouslySetInnerHTML={{ __html: article.contentHtml }}
                />

                <div className="mt-12">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                    Frequently Asked Questions
                  </h2>
                  <div className="flex flex-col gap-3">
                    {article.faqs.map((item, idx) => (
                      <div key={idx} className="bg-background-100 rounded-lg border border-background-200/70 p-4 md:p-5">
                        <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                          {item.question}
                        </h3>
                        <p className="text-foreground-600 text-sm leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-12 p-6 md:p-8 bg-primary-50 rounded-lg border border-primary-200 text-center">
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">
                    Ready to Start Shopping Smarter?
                  </h3>
                  <p className="text-foreground-600 text-sm mb-5 max-w-md mx-auto">
                    Browse the complete AcBuy Spreadsheet with 14 categories, daily updates, and trusted shopping resources — all in one place.
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Link
                      to="/"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Back to Homepage
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 hover:bg-foreground-800 text-background-50 font-semibold text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Buy It Now
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>

                <nav className="mt-10 pt-6 border-t border-background-200/70 flex items-center gap-2 text-sm">
                  <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium transition-colors whitespace-nowrap">
                    ← Back to Homepage
                  </Link>
                </nav>
              </div>
            </div>
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: article.h1,
                description: article.excerpt,
                datePublished: '2026-06-01',
                dateModified: '2026-06-24',
                author: { '@type': 'Organization', name: 'AcBuy Spreadsheet' },
                publisher: { '@type': 'Organization', name: 'AcBuy Spreadsheet', url: SITE_URL },
                mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
                  { '@type': 'ListItem', position: 2, name: article.title, item: articleUrl },
                ],
              }),
            }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: article.faqs.map((item) => ({
                  '@type': 'Question',
                  name: item.question,
                  acceptedAnswer: { '@type': 'Answer', text: item.answer },
                })),
              }),
            }}
          />
        </article>
      </main>
      <Footer />
    </>
  );
}